import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Chip, Dialog, Link, Paper, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { PhotoHistoryItem, PhotoModerationService } from 'us-web-services';
import { colors } from 'us-react-components/dist/constants/Styles';
import {
  DEFAULT_BORDER_COLOR,
  STATUS_BORDER_COLOR,
  STATUS_CHIP_COLOR,
} from '../photo-moderation/constants';

interface Props {
  userId: number;
}

function UserPhotoHistory({ userId }: Props) {
  const [photos, setPhotos] = useState<PhotoHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    setLoading(true);
    (async () => {
      try {
        const response = await PhotoModerationService.getHistory(userId);

        if (cancelled) return;
        const data = response.data.data;

        setPhotos(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn('Failed to fetch photo history:', err);
        if (!cancelled) setPhotos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const sorted = useMemo(
    () =>
      [...photos].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
      ),
    [photos],
  );

  if (loading || sorted.length === 0) return null;

  return (
    <Paper variant='outlined' sx={{ p: 2, mt: 2 }}>
      <Box display='flex' alignItems='center' mb={1}>
        <Link
          component='button'
          type='button'
          underline='hover'
          onClick={() => setExpanded(prev => !prev)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <Typography variant='subtitle1' fontWeight='medium'>
            Photo History ({sorted.length})
          </Typography>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </Link>
      </Box>
      {expanded && (
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
          {sorted.map(photo => (
            <Box
              key={photo.id}
              sx={{ flex: '0 0 auto', textAlign: 'center', width: 100 }}
            >
              {photo.photoUrl ? (
                <ButtonBase
                  onClick={() => setLightboxUrl(photo.photoUrl)}
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '2px solid',
                    borderColor: STATUS_BORDER_COLOR[photo.status] || DEFAULT_BORDER_COLOR,
                  }}
                >
                  <img
                    src={photo.photoUrl}
                    alt={`Sitter upload ${photo.id}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </ButtonBase>
              ) : (
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 1,
                    bgcolor: colors.gray[100],
                    border: '2px solid',
                    borderColor: STATUS_BORDER_COLOR[photo.status] || DEFAULT_BORDER_COLOR,
                  }}
                />
              )}
              <Chip
                label={photo.status}
                color={STATUS_CHIP_COLOR[photo.status] || 'default'}
                size='small'
                sx={{ mt: 0.5, height: 18, fontSize: 10 }}
              />
              <Typography
                variant='caption'
                display='block'
                color='textSecondary'
                sx={{ fontSize: 10 }}
              >
                {new Date(photo.created).toLocaleDateString()}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth='md'
      >
        {lightboxUrl && (
          <ButtonBase
            onClick={() => setLightboxUrl(null)}
            sx={{ display: 'block' }}
          >
            <img
              src={lightboxUrl}
              alt='Full size sitter upload'
              style={{
                display: 'block',
                maxWidth: '90vw',
                maxHeight: '90vh',
              }}
            />
          </ButtonBase>
        )}
      </Dialog>
    </Paper>
  );
}

export default UserPhotoHistory;
